import { useTranslation } from 'react-i18next';
import { getAnchorStyle } from '@/lib/watermark-anchor-style';
import type { WatermarkConfig } from '@/lib/campaign-watermark-types';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import CampaignCompositePreview, {
  type CompositeSubtitlePosition,
} from '@/components/admin/CampaignCompositePreview';

type Props = {
  config: Omit<WatermarkConfig, 'imageUrl'> & { imageUrl: string | null };
  /** Other campaign enforcements, composed into the vertical preview for consistency. */
  postLayout?: PostLayoutConfig | null;
  subtitlePosition?: CompositeSubtitlePosition | null;
};

const HorizontalPreviewFrame: React.FC<{
  config: Props['config'];
  label: string;
}> = ({ config, label }) => (
  <div className="space-y-1">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="w-[320px] h-[180px] relative rounded-md overflow-hidden bg-black">
      <video
        src="/videos/sample-video-horizontal.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />
      {config.imageUrl && (
        <img
          src={config.imageUrl}
          alt=""
          style={{
            position: 'absolute',
            height: `${config.sizePercent}%`,
            width: 'auto',
            objectFit: 'contain',
            opacity: config.opacityPercent / 100,
            ...getAnchorStyle(config.anchor, config.marginXPercent, config.marginYPercent),
          }}
        />
      )}
    </div>
  </div>
);

export const WatermarkPreviewPane: React.FC<Props> = ({ config, postLayout, subtitlePosition }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-4">
      {/* Vertical frame composes everything the campaign enforces (post
          layouts and subtitle positions only apply to vertical exports). */}
      <CampaignCompositePreview
        testId="watermark-preview-vertical"
        label={t('admin.campaigns.edit.watermark.verticalFrame')}
        postLayout={postLayout}
        watermark={config}
        subtitlePosition={subtitlePosition}
      />
      <HorizontalPreviewFrame
        config={config}
        label={t('admin.campaigns.edit.watermark.horizontalFrame')}
      />
    </div>
  );
};

export default WatermarkPreviewPane;
